import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Headphones } from "lucide-react";
import { DISCORD_URL } from "@/components/shared";

const WIDGET_URL = "https://discord.com/api/guilds/1558588111032942784/widget.json";
const BACKEND_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCountHistory() {
  return useQuery({
    queryKey: ["widget-history"],
    queryFn: async () => {
      const res = await fetch(`${BACKEND_URL}/widget/history`);
      if (!res.ok) throw new Error("history unavailable");
      const json = await res.json();
      return json.points || [];
    },
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: 1,
  });
}

function Sparkline({ points }) {
  if (!points || points.length === 0) return null;
  const w = 150;
  const h = 48;
  const pad = 4;
  const counts = points.map((p) => p.count);
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  const range = max - min || 1;
  const step = (w - pad * 2) / Math.max(points.length - 1, 1);
  const coords = points.map((p, i) => [
    +(pad + i * step).toFixed(1),
    +(h - pad - ((p.count - min) / range) * (h - pad * 2)).toFixed(1),
  ]);
  const collecting = points.length === 1;
  const line = coords.map((c) => c.join(",")).join(" ");
  const area = `${pad},${h - pad} ${line} ${w - pad},${h - pad}`;
  const last = coords[coords.length - 1];
  const flatY = h - pad - ((counts[0] - min) / range) * (h - pad * 2);

  return (
    <div data-testid="member-count-sparkline" className="text-right">
      <svg width={w} height={h} className="block">
        <defs>
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {!collecting && <polygon points={area} fill="url(#sparkFill)" />}
        {collecting ? (
          <line
            x1={pad}
            y1={flatY}
            x2={w - pad}
            y2={flatY}
            stroke="#c084fc"
            strokeWidth="1.5"
            strokeDasharray="4 5"
            strokeLinecap="round"
          />
        ) : (
          <polyline
            points={line}
            fill="none"
            stroke="#c084fc"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        <circle cx={last[0]} cy={last[1]} r="2.5" fill="#e9d5ff" />
      </svg>
      <p className="text-[11px] text-slate-500 -mt-1.5">
        {collecting ? "online · collecting history" : "online · past 24 hours"}
      </p>
    </div>
  );
}

export function useDiscordWidget() {
  return useQuery({
    queryKey: ["discord-widget"],
    queryFn: async () => {
      const res = await fetch(WIDGET_URL);
      if (!res.ok) throw new Error("widget unavailable");
      return res.json();
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

function StatusDot() {
  return (
    <span className="relative flex h-2.5 w-2.5 shrink-0">
      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.6)]" />
    </span>
  );
}

export function VoiceChips({ channels, size = "sm" }) {
  if (!channels || channels.length === 0) return null;
  const cls =
    size === "sm"
      ? "gap-1.5 px-2.5 py-1 text-xs"
      : "gap-2 px-3.5 py-1.5 text-sm";
  return (
    <div className="flex flex-wrap items-center gap-2">
      {channels.map((ch) => (
        <span
          key={ch.id}
          data-testid={`voice-channel-${ch.id}`}
          className={`inline-flex items-center gap-1.5 rounded-full border border-purple-500/20 bg-purple-500/5 font-medium text-purple-300 ${cls}`}
        >
          <Headphones className="w-3.5 h-3.5 text-purple-400" />
          {ch.name}
        </span>
      ))}
    </div>
  );
}

export function DiscordWidget() {
  const { data } = useDiscordWidget();
  const { data: history } = useCountHistory();

  if (!data) return null;
  const members = (data.members || []).filter((m) => m.avatar_url).slice(0, 8);
  const count = data.presence_count;
  const peak =
    history && history.length > 0
      ? Math.max(...history.map((p) => p.count))
      : null;

  return (
    <div
      data-testid="discord-widget"
      className="relative overflow-hidden rounded-2xl border border-purple-500/15 bg-[#11131f]/80 p-6 sm:p-8"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(147,51,234,0.14) 0%, rgba(9,10,15,0) 65%)",
        }}
      />
      <div className="relative">
        <div className="flex flex-wrap items-center gap-3">
          <StatusDot />
          <p className="text-sm font-medium text-slate-300">
            {data.name}
            <span className="text-slate-500"> — live from Discord</span>
          </p>
        </div>

        <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
          <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            {typeof count === "number" ? count.toLocaleString() : "—"}
            <span className="ml-2.5 text-base font-semibold text-slate-400">
              members online now
            </span>
          </p>
          <div className="flex flex-col items-end gap-1.5">
            <Sparkline points={history} />
            {peak != null && (
              <p
                data-testid="member-count-peak"
                className="text-xs font-semibold text-emerald-300"
              >
                24h peak: {peak.toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {members.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="flex -space-x-2.5">
              {members.map((m) => (
                <img
                  key={m.id}
                  src={m.avatar_url}
                  alt={m.username}
                  className="w-9 h-9 rounded-full border-2 border-[#11131f] object-cover"
                  loading="lazy"
                />
              ))}
            </div>
            <span className="text-sm text-slate-500">
              hanging out in the community right now
            </span>
          </div>
        )}

        {(data.channels || []).length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">
              Voice channels
            </p>
            <VoiceChips channels={data.channels} size="md" />
          </div>
        )}

        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="discord-widget-join-button"
          className="btn-glow mt-7 inline-flex items-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_25px_rgba(168,85,247,0.45)] hover:shadow-[0_0_35px_rgba(168,85,247,0.7)]"
        >
          Join them
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

export function DiscordWidgetCompact() {
  const { data } = useDiscordWidget();

  if (!data || typeof data.presence_count !== "number") return null;
  const channels = data.channels || [];

  return (
    <div
      data-testid="discord-widget-compact"
      className="relative overflow-hidden rounded-2xl border border-purple-500/15 bg-[#11131f]/80"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 0% 0%, rgba(147,51,234,0.14) 0%, rgba(9,10,15,0) 55%)",
        }}
      />
      <div className="relative flex flex-wrap items-center justify-between gap-x-6 gap-y-4 px-6 py-5 sm:px-8">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="inline-flex items-center gap-2.5">
            <StatusDot />
            <span className="text-sm font-semibold text-white">
              {data.presence_count.toLocaleString()} online
            </span>
          </span>
          {channels.length > 0 && (
            <span className="hidden sm:block h-4 w-px bg-purple-500/20" aria-hidden="true" />
          )}
          <VoiceChips channels={channels} size="sm" />
        </div>
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="discord-widget-compact-join-button"
          className="btn-glow inline-flex items-center gap-2 rounded-lg bg-purple-600 hover:bg-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.65)]"
        >
          Join the Discord
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

export function LiveCountBadge() {
  const { data } = useDiscordWidget();

  if (!data || typeof data.presence_count !== "number") return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.7 }}
      data-testid="live-count-badge"
      className="inline-flex items-center gap-2.5 rounded-full border border-emerald-400/20 bg-emerald-400/5 px-4 py-2"
    >
      <StatusDot />
      <span className="text-xs sm:text-sm font-medium text-emerald-300">
        {data.presence_count.toLocaleString()} members online now
      </span>
    </motion.div>
  );
}
